import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import {
  useCreateCustomVariety,
  useGetSuggestedCustomVariety,
} from '@/modules/settings/property/apiHooks';
import { IPropertySettings } from '@/modules/settings/property/types';
import { Button, InputField } from '@/shared/components/atoms';
import { SuggestionsList } from '@/shared/components/molecules/SuggestionsList';
import { useStore } from '@/shared/hooks/context/createGenericContext';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';
import useDebounce from '@/shared/hooks/useDebounce';

const AddCustomVariety: React.FC<{
  cropId: number;
  onSuccessFunction?: () => void;
}> = ({ cropId, onSuccessFunction }) => {
  const { closeModal } = useModal();
  const { t } = useTranslation('common');
  const divRef = useRef<HTMLDivElement>(null);
  const [search, setSearch] = useState<string | undefined>('');
  const [showList, setShowList] = useState<boolean>(true);
  const [selectedAsset] = useStore((store) => store?.asset);

  const { mutate, isLoading } = useCreateCustomVariety();
  const suggestedCustomVarietyList = useGetSuggestedCustomVariety({
    cropId,
    entityId: selectedAsset?.id,
    search,
  });

  const schema = yup.object({
    varietyName: yup
      .string()
      .required(t('this-field-is-required'))
      .min(1, t('this-field-is-required')),
  });
  const formMethods = useForm<
    Omit<IPropertySettings.ICreateCustomVarietySchema, 'entityId' | 'cropId'>
  >({
    resolver: yupResolver(schema),
  });

  const {
    watch,
    register,
    setValue,
    handleSubmit,
    formState: { errors },
  } = formMethods;

  const varietyNameWatcher = watch('varietyName');
  const debouncedSearch = useDebounce(varietyNameWatcher, 500);

  useEffect(() => {
    setShowList(true);
    setSearch(debouncedSearch);
  }, [debouncedSearch]);

  const handleNameSelection = (name: string) => {
    setValue('varietyName', name);
    setShowList(false);
    setSearch(undefined);
  };

  const onSubmit = (
    data: Omit<
      IPropertySettings.ICreateCustomVarietySchema,
      'entityId' | 'cropId'
    >
  ) => {
    if (selectedAsset) {
      mutate(
        {
          entityId: selectedAsset.id,
          varietyName: data.varietyName,
          cropId,
        },
        {
          onSuccess: () => {
            if (onSuccessFunction) onSuccessFunction();
            closeModal();
            toast.success(t('crop-added'));
          },
        }
      );
    }
  };
  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className='relative flex flex-col gap-16' ref={divRef}>
          <InputField
            borderColor='border-outline-variant'
            width='w-full'
            py='!py-16'
            label={t('custom-variety-name')}
            placeholder={t('custom-variety-name')}
            error={!!errors.varietyName}
            required
            errorMessage={errors.varietyName?.message}
            className='!rounded-xl bg-background-transparent'
            {...register('varietyName')}
          />
          {search && showList && (
            <SuggestionsList
              requestInfo={suggestedCustomVarietyList}
              divRef={divRef}
              setNameValue={handleNameSelection}
              placeholder={t('suggested-variety')}
            />
          )}
        </div>

        <Button
          disabled={isLoading}
          type='submit'
          className='mt-32 h-56 !w-full !rounded-xl'
        >
          {t('confirm')}
        </Button>
      </form>
    </FormProvider>
  );
};

export { AddCustomVariety };
